import type {
  FieldDefinition,
  SourceDescriptor,
  TableSourceDescriptor,
  VectorFeatureCollection,
  VectorSourceDescriptor,
} from '../data/model';
import type { LayerDefinition } from '../project/model';
import type { JoinConfig, JoinedFieldMapping } from './model';

export interface NativeEffectiveFieldOrigin {
  readonly kind: 'native';
}

export interface JoinedEffectiveFieldOrigin {
  readonly kind: 'joined';
  readonly joinId: string;
  readonly tableSourceId: string;
  readonly sourceField: string;
}

export interface EffectiveFieldDefinition extends FieldDefinition {
  readonly displayLabel: string;
  readonly origin: NativeEffectiveFieldOrigin | JoinedEffectiveFieldOrigin;
}

interface EffectiveCache {
  readonly vectorData: VectorFeatureCollection;
  readonly tableSources: readonly TableSourceDescriptor[];
  readonly source: VectorSourceDescriptor;
}

const effectiveCache = new WeakMap<readonly JoinConfig[], EffectiveCache>();

export function fieldDisplayLabel(field: FieldDefinition): string {
  return 'displayLabel' in field && typeof field.displayLabel === 'string'
    ? field.displayLabel
    : field.name;
}

export function isJoinedEffectiveField(
  field: FieldDefinition,
): field is EffectiveFieldDefinition & { readonly origin: JoinedEffectiveFieldOrigin } {
  return (
    'origin' in field &&
    typeof field.origin === 'object' &&
    field.origin !== null &&
    'kind' in field.origin &&
    field.origin.kind === 'joined'
  );
}

function joinedField(
  join: JoinConfig,
  mapping: JoinedFieldMapping,
  table: TableSourceDescriptor,
): EffectiveFieldDefinition | null {
  const source = table.fields.find((field) => field.name === mapping.sourceField);
  return source
    ? {
        ...source,
        name: mapping.outputFieldId,
        displayLabel: mapping.displayLabel,
        nullable: true,
        origin: {
          kind: 'joined',
          joinId: join.id,
          tableSourceId: table.id,
          sourceField: source.name,
        },
      }
    : null;
}

export function effectiveFieldsForLayer(
  sources: readonly SourceDescriptor[],
  layer: LayerDefinition,
): readonly EffectiveFieldDefinition[] {
  const vector = sources.find(
    (source): source is VectorSourceDescriptor =>
      source.id === layer.sourceId && source.kind === 'vector',
  );
  if (!vector) return [];
  return effectiveFieldsForJoins(sources, vector, layer.joins ?? []);
}

export function effectiveFieldsForJoins(
  sources: readonly SourceDescriptor[],
  vector: VectorSourceDescriptor,
  joins: readonly JoinConfig[],
): readonly EffectiveFieldDefinition[] {
  const native = vector.fields.map((field) => ({
    ...field,
    displayLabel: field.name,
    origin: { kind: 'native' as const },
  }));
  const joined = joins.flatMap((join) => {
    const table = sources.find(
      (source): source is TableSourceDescriptor =>
        source.id === join.tableSourceId && source.kind === 'table',
    );
    return table
      ? join.fields.flatMap((mapping) => {
          const field = joinedField(join, mapping, table);
          return field ? [field] : [];
        })
      : [];
  });
  return [...native, ...joined];
}

function tableLookup(table: TableSourceDescriptor, join: JoinConfig): ReadonlyMap<string, number> {
  const field = table.fields.find((candidate) => candidate.name === join.rightField);
  const column = table.fields.findIndex((candidate) => candidate.name === join.rightField);
  const kind = field?.type === 'integer' || field?.type === 'number' ? 'number' : 'string';
  const lookup = new Map<string, number>();
  if (column < 0) return lookup;
  for (let index = 0; index < table.rows.length; index += 1) {
    const value = table.rows[index]?.[column];
    if (value === null || value === undefined || value === '') continue;
    lookup.set(
      `${kind === 'number' ? 'n' : 's'}:${String(Object.is(value, -0) ? 0 : value)}`,
      index,
    );
  }
  return lookup;
}

function joinData(
  vector: VectorSourceDescriptor,
  tables: readonly TableSourceDescriptor[],
  joins: readonly JoinConfig[],
): VectorFeatureCollection {
  const prepared = joins.map((join, index) => {
    const table = tables[index] as TableSourceDescriptor;
    const left = vector.fields.find((field) => field.name === join.leftField);
    const kind = left?.type === 'integer' || left?.type === 'number' ? 'number' : 'string';
    return {
      join,
      table,
      kind,
      lookup: tableLookup(table, join),
      outputColumns: join.fields.map((mapping) => ({
        mapping,
        column: table.fields.findIndex((field) => field.name === mapping.sourceField),
      })),
    };
  });
  return {
    type: 'FeatureCollection',
    features: vector.data.features.map((feature) => {
      const properties = { ...(feature.properties ?? {}) };
      for (const item of prepared) {
        const value = feature.properties?.[item.join.leftField];
        const key =
          value === null || value === undefined || value === ''
            ? null
            : `${item.kind === 'number' ? 'n' : 's'}:${String(Object.is(value, -0) ? 0 : value)}`;
        const rowIndex = key === null ? undefined : item.lookup.get(key);
        const row = rowIndex === undefined ? undefined : item.table.rows[rowIndex];
        for (const output of item.outputColumns) {
          properties[output.mapping.outputFieldId] =
            row && output.column >= 0 ? (row[output.column] ?? null) : null;
        }
      }
      return { ...feature, geometry: feature.geometry, properties };
    }),
  };
}

export function effectiveVectorSourceForLayer(
  sources: readonly SourceDescriptor[],
  layer: LayerDefinition,
): VectorSourceDescriptor | null {
  const vector = sources.find(
    (source): source is VectorSourceDescriptor =>
      source.id === layer.sourceId && source.kind === 'vector',
  );
  if (!vector) return null;
  const joins = layer.joins ?? [];
  if (joins.length === 0) return vector;
  const tables = joins.map((join) =>
    sources.find(
      (source): source is TableSourceDescriptor =>
        source.id === join.tableSourceId && source.kind === 'table',
    ),
  );
  if (tables.some((table) => !table)) return vector;
  const presentTables = tables as readonly TableSourceDescriptor[];
  const cached = effectiveCache.get(joins);
  if (
    cached?.vectorData === vector.data &&
    cached.tableSources.length === presentTables.length &&
    cached.tableSources.every((table, index) => table === presentTables[index])
  ) {
    return cached.source;
  }
  const source: VectorSourceDescriptor = {
    ...vector,
    data: joinData(vector, presentTables, joins),
    fields: effectiveFieldsForLayer(sources, layer),
  };
  effectiveCache.set(joins, { vectorData: vector.data, tableSources: presentTables, source });
  return source;
}
